/* WrenchConnected — Installation Availability JS.
 *
 * Wires all interactivity for both surfaces on the dpp-wrench theme:
 *   1. .wc-home-banner       — homepage banner CTA opens the zip modal
 *   2. .wc-home-modal-*      — zip modal POSTs to Supabase, renders result
 *   3. .wc-info-modal-*      — "How it works" modal triggered from inside
 *                              the zip modal (or from the PDP if present)
 *   4. .wc-pdp-installer     — product page collapsible row + zip widget
 *
 * All three share a sessionStorage cache so a customer who checks
 * their zip in any surface sees the same prefilled result in every
 * other surface for the next hour.
 */
(function () {
  'use strict';

  var ENDPOINT =
    'https://kebmeptrahnivkypuvsu.supabase.co/functions/v1/check-installation-availability';
  var CACHE_PREFIX = 'wc-zip:';
  var CACHE_TTL_MS = 60 * 60 * 1000;
  var ZIP_RX = /^\d{5}$/;

  function readCache(zip) {
    try {
      var raw = window.sessionStorage.getItem(CACHE_PREFIX + zip);
      if (!raw) return null;
      var p = JSON.parse(raw);
      if (!p || typeof p.t !== 'number') return null;
      if (Date.now() - p.t > CACHE_TTL_MS) return null;
      return p.r;
    } catch (_) { return null; }
  }
  function writeCache(zip, r) {
    try {
      window.sessionStorage.setItem(
        CACHE_PREFIX + zip,
        JSON.stringify({ t: Date.now(), r: r })
      );
    } catch (_) {}
  }
  function checkZip(zip) {
    return fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ zip: zip })
    }).then(function (res) {
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return res.json();
    });
  }
  function prefillFromSession(input, render) {
    try {
      for (var i = 0; i < window.sessionStorage.length; i++) {
        var key = window.sessionStorage.key(i);
        if (key && key.indexOf(CACHE_PREFIX) === 0) {
          var z = key.slice(CACHE_PREFIX.length);
          var c = readCache(z);
          if (c) { input.value = z; render(z, c); return; }
        }
      }
    } catch (_) {}
  }

  /* ───────── Homepage banner + zip modal ───────── */
  function initBanner(root) {
    if (root.dataset.wcInit === '1') return;
    root.dataset.wcInit = '1';

    var trigger  = root.querySelector('[data-wc-home-trigger]');
    var backdrop = root.querySelector('[data-wc-home-backdrop]');
    var closeBtn = root.querySelector('[data-wc-home-close]');
    var form     = root.querySelector('[data-wc-home-form]');
    var input    = root.querySelector('[data-wc-home-input]');
    var submit   = root.querySelector('[data-wc-home-submit]');
    var result   = root.querySelector('[data-wc-home-result]');
    if (!backdrop || !form || !input || !submit || !result) return;

    var msgSuccess = root.dataset.msgSuccess;
    var msgUnavail = root.dataset.msgUnavail;
    var msgError   = root.dataset.msgError;

    function setResult(state, text) {
      result.dataset.state = state;
      result.textContent = text;
    }
    function render(zip, data) {
      if (data && data.available === true) {
        var c = (typeof data.shop_count === 'number') ? data.shop_count : 0;
        setResult('success',
          c > 0 ? msgSuccess + ' (' + c + ' installer' + (c === 1 ? '' : 's') + ' nearby)'
                : msgSuccess);
      } else {
        setResult('unavail', msgUnavail);
      }
    }
    function open()  { backdrop.dataset.open = 'true';  setTimeout(function(){input.focus();}, 60); document.addEventListener('keydown', onKey); }
    function close() { backdrop.dataset.open = 'false'; document.removeEventListener('keydown', onKey); }
    function onKey(e) { if (e.key === 'Escape') close(); }

    if (trigger)  trigger.addEventListener('click', open);
    if (closeBtn) closeBtn.addEventListener('click', close);
    backdrop.addEventListener('click', function (e) { if (e.target === backdrop) close(); });

    input.addEventListener('input', function () {
      var v = (input.value || '').replace(/[^0-9]/g, '').slice(0, 5);
      if (v !== input.value) input.value = v;
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var zip = (input.value || '').trim();
      if (!ZIP_RX.test(zip)) { setResult('error', 'Please enter a valid 5-digit zip code.'); return; }
      var cached = readCache(zip);
      if (cached) { render(zip, cached); return; }
      submit.disabled = true;
      setResult('loading', 'Checking…');
      checkZip(zip)
        .then(function (data) { writeCache(zip, data); render(zip, data); })
        .catch(function () { setResult('error', msgError); })
        .finally(function () { submit.disabled = false; });
    });

    prefillFromSession(input, render);
  }

  /* ───────── "How it works" info modal ───────── */
  function initInfoModal(root) {
    if (root.dataset.wcInfoInit === '1') return;
    root.dataset.wcInfoInit = '1';

    var trigger  = root.querySelector('[data-wc-info-trigger]');
    var backdrop = root.querySelector('[data-wc-info-backdrop]');
    if (!trigger || !backdrop) return;

    var closeBtn = backdrop.querySelector('[data-wc-info-close]');
    function open()  { backdrop.dataset.open = 'true';  document.addEventListener('keydown', onKey); }
    function close() { backdrop.dataset.open = 'false'; document.removeEventListener('keydown', onKey); }
    function onKey(e) { if (e.key === 'Escape') close(); }

    trigger.addEventListener('click', open);
    if (closeBtn) closeBtn.addEventListener('click', close);
    backdrop.addEventListener('click', function (e) { if (e.target === backdrop) close(); });
  }

  /* ───────── Product page block (collapsible + zip widget) ───────── */
  function initPdp(root) {
    if (root.dataset.wcInit === '1') return;
    root.dataset.wcInit = '1';

    var trigger = root.querySelector('[data-wc-pdp-trigger]');
    var form    = root.querySelector('[data-wc-pdp-form]');
    var input   = root.querySelector('[data-wc-pdp-input]');
    var submit  = root.querySelector('[data-wc-pdp-submit]');
    var result  = root.querySelector('[data-wc-pdp-result]');
    if (!trigger || !form || !input || !submit || !result) return;

    var msgSuccess = root.dataset.msgSuccess;
    var msgUnavail = root.dataset.msgUnavail;
    var msgError   = root.dataset.msgError;

    function setResult(state, text) {
      result.dataset.state = state;
      result.textContent = text;
    }
    function render(zip, data) {
      if (data && data.available === true) {
        var c = (typeof data.shop_count === 'number') ? data.shop_count : 0;
        setResult('success',
          c > 0 ? msgSuccess + ' (' + c + ' installer' + (c === 1 ? '' : 's') + ' nearby)'
                : msgSuccess);
      } else {
        setResult('unavail', msgUnavail);
      }
    }

    trigger.addEventListener('click', function () {
      var expanded = trigger.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      if (!expanded) setTimeout(function () { input.focus(); }, 200);
    });

    input.addEventListener('input', function () {
      var v = (input.value || '').replace(/[^0-9]/g, '').slice(0, 5);
      if (v !== input.value) input.value = v;
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var zip = (input.value || '').trim();
      if (!ZIP_RX.test(zip)) { setResult('error', 'Please enter a valid 5-digit zip code.'); return; }
      var cached = readCache(zip);
      if (cached) { render(zip, cached); return; }
      submit.disabled = true;
      setResult('loading', 'Checking…');
      checkZip(zip)
        .then(function (data) { writeCache(zip, data); render(zip, data); })
        .catch(function () { setResult('error', msgError); })
        .finally(function () { submit.disabled = false; });
    });

    prefillFromSession(input, render);
  }

  function initAll() {
    var banners = document.querySelectorAll('.wc-home-banner');
    for (var i = 0; i < banners.length; i++) {
      initBanner(banners[i]);
      // The info modal markup is rendered inside the banner aside as well.
      initInfoModal(banners[i]);
    }
    var pdps = document.querySelectorAll('.wc-pdp-installer');
    for (var j = 0; j < pdps.length; j++) {
      initPdp(pdps[j]);
      // Each PDP block renders its own info-modal snippet, so wire the
      // trigger + backdrop the same way the banner does.
      initInfoModal(pdps[j]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:block:select', initAll);
})();
